<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRegistry } from '@hantera/portal-app'
import { TextInput } from '@hantera/design-system'

const registry = useRegistry()

const props = defineProps<{
  modelValue: string | Record<string, string>
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | Record<string, string>): void
}>()

interface LocaleInfo {
  key: string
  label: string
  language: string
}

const locales = ref<LocaleInfo[]>([])
const expanded = ref(typeof props.modelValue === 'object')

onMounted(async () => {
  try {
    const data = await registry.list('locales', true)
    locales.value = Object.entries(data).map(([fullKey, value]) => {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value
      return {
        key: fullKey.split('/').pop()!,
        label: parsed?.label || '',
        language: parsed?.language || '',
      }
    }).sort((a, b) => a.key.localeCompare(b.key))
  } catch {
    locales.value = []
  }
})

watch(() => props.modelValue, (val) => {
  if (typeof val === 'object') {
    expanded.value = true
  }
})

const defaultValue = computed(() => {
  if (typeof props.modelValue === 'string') return props.modelValue
  return props.modelValue?.['default'] ?? ''
})

function getLocaleValue(localeKey: string): string {
  if (typeof props.modelValue === 'string') return ''
  return props.modelValue?.[localeKey] ?? ''
}

function updateSimple(value: string) {
  emit('update:modelValue', value)
}

function updateDefault(value: string) {
  const record: Record<string, string> = typeof props.modelValue === 'object'
    ? { ...props.modelValue }
    : { default: '' }
  record['default'] = value
  emitRecord(record)
}

function updateLocale(localeKey: string, value: string) {
  const record: Record<string, string> = typeof props.modelValue === 'object'
    ? { ...props.modelValue }
    : { default: defaultValue.value }
  if (value) {
    record[localeKey] = value
  } else {
    delete record[localeKey]
  }
  emitRecord(record)
}

function emitRecord(record: Record<string, string>) {
  const nonDefaultKeys = Object.keys(record).filter(k => k !== 'default' && record[k])
  if (nonDefaultKeys.length === 0) {
    emit('update:modelValue', record['default'] ?? '')
  } else {
    emit('update:modelValue', record)
  }
}

function expand() {
  const current = typeof props.modelValue === 'string' ? props.modelValue : (props.modelValue?.['default'] ?? '')
  expanded.value = true
  emit('update:modelValue', { default: current })
}

function collapse() {
  expanded.value = false
  emit('update:modelValue', defaultValue.value)
}
</script>

<template>
  <div class="localized-label-input">
    <template v-if="!expanded">
      <div class="localized-label-input__simple">
        <TextInput
          :model-value="defaultValue"
          @update:model-value="updateSimple"
          :disabled="disabled"
        />
        <button
          v-if="!disabled && locales.length > 0"
          class="btn-silent localized-label-input__expand"
          @click="expand"
          title="Add translations"
        >🌐</button>
      </div>
    </template>

    <template v-else>
      <div class="localized-label-input__localized">
        <div class="localized-label-input__row">
          <span class="localized-label-input__locale-key">default</span>
          <TextInput
            :model-value="defaultValue"
            @update:model-value="updateDefault"
            :disabled="disabled"
          />
          <button
            v-if="!disabled"
            class="btn-silent localized-label-input__collapse"
            @click="collapse"
            title="Remove translations"
          >✕</button>
        </div>

        <div
          v-for="locale in locales"
          :key="locale.key"
          class="localized-label-input__row"
        >
          <span class="localized-label-input__locale-key" :title="locale.language">
            {{ locale.label || locale.key }}
          </span>
          <TextInput
            :model-value="getLocaleValue(locale.key)"
            @update:model-value="v => updateLocale(locale.key, v)"
            :disabled="disabled"
            :placeholder="defaultValue"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.localized-label-input__simple {
  display: flex;
  align-items: center;
  gap: 4px;
}

.localized-label-input__simple> :first-child {
  flex: 1;
}

.localized-label-input__expand {
  width: 28px;
  height: 28px;
  font-size: 16px;
  padding: 0;
  flex-shrink: 0;
}

.localized-label-input__localized {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.localized-label-input__row {
  display: grid;
  grid-template-columns: 100px 1fr auto;
  align-items: center;
  gap: 8px;
}

.localized-label-input__locale-key {
  font-size: 0.8rem;
  color: hsl(var(--color-40));
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.localized-label-input__collapse {
  width: 24px;
  height: 24px;
  font-size: 14px;
  padding: 0;
}
</style>
